<template>
  <div class="single-upload-form">
    <!-- Form and file inputs -->
    <div class="form-and-file-inputs">
      <PVForm
        v-if="contentTypeFieldsForForm"
        id="metadata-form"
        v-slot="$form"
        :initial-values="initialValues"
        :resolver="resolver"
        @submit="handleFormSubmit"
      >
        <PVFormField
          v-for="field in contentTypeFieldsForForm"
          v-slot="$field"
          :key="field.field"
          :name="field.field"
        >
          <label :for="field.fieldDisplayName">{{
            getFieldLabel(field)
          }}</label>
          <div
            v-if="field.dataType === ContentTypeFieldType.TAXONOMY_FIELD_TYPE"
          >
            <styled-multiselect
              v-if="$form[field.field]"
              v-model="$field.value"
              :is-error="$field?.invalid"
              :options="field.possibleValues!"
              :placeholder="`${t('generic.forExample')} ${
                field.possibleValues?.[0]
              }`"
              @select="(e) => ($field as any).onInput({ value: e })"
            ></styled-multiselect>
            <message-wrapper v-if="$field?.invalid" simple-error>{{
              $field.error.message
            }}</message-wrapper>
            <div v-else class="spacer"></div>
          </div>
          <div
            v-else-if="
              field.dataType === ContentTypeFieldType.TAXONOMY_FIELD_TYPE_MULTI
            "
          >
            <styled-tags-input
              :add-only-from-autocomplete="true"
              :autocomplete-items="field.possibleValues"
              :is-error="$field?.invalid"
              :model-value="
                $field.value?.split(DEFAULT_METADATA_VALUES_SEPARATOR) ?? []
              "
              @tags-changed="
                (e) =>
                  ($field as any).onInput({
                    value: e?.join(DEFAULT_METADATA_VALUES_SEPARATOR),
                  })
              "
            ></styled-tags-input>
            <message-wrapper v-if="$field?.invalid" simple-error>{{
              $field.error.message
            }}</message-wrapper>
            <div v-else class="spacer"></div>
          </div>
          <div v-else-if="field.dataType === ContentTypeFieldType.DATE_TIME">
            <PVDatePicker fluid />
            <message-wrapper v-if="$field?.invalid" simple-error>{{
              $field.error.message
            }}</message-wrapper>
            <div v-else class="spacer"></div>
          </div>
          <div v-else>
            <PVInputText fluid :placeholder="field.field" />
            <message-wrapper v-if="$field?.invalid" simple-error>{{
              $field.error.message
            }}</message-wrapper>
            <div v-else class="spacer"></div>
          </div>
        </PVFormField>
      </PVForm>
      <single-upload-file-inputs
        :is-uploading="isUploading"
        :upload-progress="uploadProgress"
        @main-file-selected="handleFile($event, DocumentKind.MAIN)"
        @rendition-file-selected="handleFile($event, DocumentKind.RENDITION)"
      />
    </div>
    <div class="submit-buttons">
      <vf-button
        id="cancel-button"
        :variant="buttonVariants.tertiary"
        :size="buttonSizes.large"
        @click="handleCancel"
      >
        {{ t('generic.cancel') }}
      </vf-button>
      <vf-button
        :busy="isUploading || isBusy"
        form="metadata-form"
        :variant="ButtonVariant.PRIMARY"
        :size="ButtonSize.LARGE"
        type="submit"
      >
        {{ t('documentUpload.uploadFile') }}
      </vf-button>
    </div>

    <!-- Error messages -->
    <div class="error-messages">
      <message-wrapper v-if="isMainFileMissingError" severity="error">
        {{ t('documentUpload.errors.singleUploadMainFileMissing') }}
      </message-wrapper>
      <message-wrapper v-if="isRenditionFileMissingError" severity="error">
        {{ t('documentUpload.errors.singleUploadRenditionFileMissing') }}
      </message-wrapper>
      <slot name="additional-error-messages" />
    </div>
  </div>
</template>

<script lang="ts" setup>
  import StyledMultiselect from '@/components/StyledMultiselect/StyledMultiselect.vue';
  import StyledTagsInput from '@/components/StyledTagsInput/StyledTagsInput.vue';
  import { FormSubmitEvent } from '@primevue/forms/form';
  import { zodResolver } from '@primevue/forms/resolvers/zod';
  import PVInputText from 'primevue/inputtext';
  import {
    ContentTypeFieldType,
    DEFAULT_METADATA_VALUES_SEPARATOR,
    DocumentKind,
    IContentTypeField,
    UniversalMappingField,
  } from 'types';
  import { alphaNumericDashUnderscoreRegex, generateDocumentId } from 'utils';
  import { computed, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import z, { ZodDate, ZodOptional, ZodString, ZodType } from 'zod';
  import { ButtonSize, ButtonVariant } from '../VfButton/VfButton.types';
  import { SingleUploadFormSubmitEvent } from './SingleUploadForm.types';

  const props = withDefaults(
    defineProps<{
      contentTypeFields: IContentTypeField[];
      contentTypeId: string;
      isBusy: boolean;
      isUploading: boolean;
      isRenditionFileRequired: ((docId: string) => Promise<boolean>) | boolean;
      noRequiredFields: boolean;
      uploadProgress: number | undefined;
      displayDocumentIdField: boolean;
    }>(),
    {
      displayDocumentIdField: true,
    },
  );

  const emit = defineEmits<{
    (e: 'cancel'): void;
    (e: 'form-submit', event: SingleUploadFormSubmitEvent): void;
  }>();

  const { t } = useI18n();

  const mainFile = ref<File>();
  const renditionFile = ref<File>();
  const isMainFileMissingError = ref(false);
  const isRenditionFileMissingError = ref(false);

  const contentTypeFieldsForForm = computed<IContentTypeField[]>(() => {
    const order: string[] = [UniversalMappingField.DOCUMENT_ID];
    return (
      props.contentTypeFields
        /** Filepath is needed only for mass upload */
        .filter(
          (field) =>
            field.field !== UniversalMappingField.FILEPATH &&
            (props.displayDocumentIdField ||
              field.field !== UniversalMappingField.DOCUMENT_ID),
        )
        /** `documentId` is always first */
        .sort((a, b) => {
          const indexA = order.indexOf(a.fieldDisplayName);
          const indexB = order.indexOf(b.fieldDisplayName);
          if (indexA === -1 && indexB !== -1) return 1;
          if (indexA !== -1 && indexB === -1) return -1;
          return indexA - indexB;
        })
    );
  });

  const zodSchema = computed(() => {
    const shape: Record<string, ZodType> = {};
    contentTypeFieldsForForm.value.forEach((field) => {
      if (
        [
          ContentTypeFieldType.TEXT,
          ContentTypeFieldType.TAXONOMY_FIELD_TYPE,
          ContentTypeFieldType.TAXONOMY_FIELD_TYPE_MULTI,
          ContentTypeFieldType.NOTE,
        ].includes(field.dataType)
      ) {
        let validation: ZodString | ZodOptional<ZodString> = z.string();
        if (field.field === UniversalMappingField.DOCUMENT_ID) {
          validation = validation.regex(
            alphaNumericDashUnderscoreRegex,
            t('documentUpload.errors.invalidDocumentId'),
          );
        }
        if (!field.required || props.noRequiredFields) {
          validation = validation.optional();
        } else {
          validation = validation.min(1);
        }
        shape[field.field] = validation;
      } else if (field.dataType === ContentTypeFieldType.DATE_TIME) {
        let validation: ZodDate | ZodOptional<ZodDate> = z.date();
        if (!field.required || props.noRequiredFields) {
          validation = validation.optional();
        }
        shape[field.field] = validation;
      }
    });
    return z.object(shape);
  });
  const resolver = ref(zodResolver(zodSchema.value));

  watch(
    contentTypeFieldsForForm,
    () => {
      resolver.value = zodResolver(zodSchema.value);
    },
    { deep: true },
  );

  const initialValues = computed<Record<string, string | undefined>>(() =>
    Object.fromEntries(
      contentTypeFieldsForForm.value.map((field) => [field.field, undefined]),
    ),
  );

  function getFieldLabel(field: IContentTypeField): string {
    let label = t(`contentTypeFields.${field.field}`);
    const checkBotField = field.checkBotField;
    /** None of the fields is required in digitalization project mode */
    if (!props.noRequiredFields && field?.required) {
      label += ' *';
    }
    if (checkBotField) {
      label += ` (${checkBotField})`;
    }
    return label;
  }

  function handleFile(file: File, kind: DocumentKind): void {
    if (kind === DocumentKind.MAIN) {
      mainFile.value = file;
    } else if (kind === DocumentKind.RENDITION) {
      renditionFile.value = file;
    }
  }

  async function validateFiles(documentId: string): Promise<boolean> {
    isMainFileMissingError.value = false;
    isRenditionFileMissingError.value = false;
    if (!mainFile.value) {
      isMainFileMissingError.value = true;
      return false;
    }
    const isRenditionRequired =
      typeof props.isRenditionFileRequired === 'function'
        ? await props.isRenditionFileRequired(documentId)
        : props.isRenditionFileRequired;
    if (isRenditionRequired && !renditionFile.value) {
      isRenditionFileMissingError.value = true;
      return false;
    }
    return true;
  }

  /** TODO: document ID for workspaces?? */
  async function handleFormSubmit(event: FormSubmitEvent): Promise<void> {
    if (!event.valid) return;
    const documentId =
      event.values[UniversalMappingField.DOCUMENT_ID] ?? generateDocumentId();
    if (!(await validateFiles(documentId))) return;
    emit('form-submit', {
      formEvent: event,
      mainFile: mainFile.value!,
      renditionFile: renditionFile.value,
    });
  }

  function handleCancel(): void {
    emit('cancel');
  }
</script>
<style lang="scss" scoped>
  .single-upload-form {
    .form-and-file-inputs {
      display: flex;
      form {
        display: flex;
        flex-wrap: wrap;
        column-gap: 1.25rem;
        align-items: flex-end;
        .p-formfield {
          width: 22%;
          .spacer {
            height: 3rem;
          }
        }
      }
    }
    .error-messages {
      margin-top: 1rem;
      display: flex;
      flex-direction: column;
      row-gap: 0.5rem;
    }
    .submit-buttons {
      margin-top: 3rem;
      width: 100%;
      display: flex;
      justify-content: flex-end;
      column-gap: 1.25rem;
    }
    :deep(.multiselect__tags) {
      border-color: $border-color;
      padding-top: 10px;
      min-height: 46px;
    }
  }
</style>
